import { Fragment, useCallback, useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import {
  aggregateByLine,
  aggregateLineParties,
  aggregatePeriods,
  kmdDueDate,
  kmdDueFlow,
  kmdPastDeclarationMonths,
  lineCertaintySlice,
  paymentPlanFlow,
  payrollTax,
  resolveEntry,
  resolvePlacements,
  sumPeriods,
  taxKindOf,
  CASH_FLOW_CATEGORIES,
  type Basis,
  type CashFlowCategory,
  type CashFlowEntry as CoreEntry,
  type DayRollup as CoreDay,
  type Granularity,
  type LinePartyMatrix,
  type LineRollup,
  type PartyCell,
  type PeriodRollup,
  type Priority,
} from '@progear/finance-core';
import { cn } from '@/lib/utils';
import { useLocateScroll } from '@/hooks/useLocateScroll';
import { useHoverIntent } from '@/hooks/useHoverIntent';
import { KmdVarianceHoverCard, MatrixHoverCard, type KmdMonthStatView } from './calendar/HoverCards';
import type { HoverPos } from './calendar/model';
import type { ActiveMatch, ForecastScope } from './CashFlowWorkspace';
import { toRow, type LedgerScope, type LedgerRow } from './Ledger';
import {
  kmdBackcastShownTotal,
  kmdBackcastStats,
  kmdDeclarationMonths,
  kmdDeclarationScope,
  kmdMonthStatViews,
  KMD_BACKCAST_EARLIEST_MONTH,
  type KmdMonthStat,
} from './kmdScope';
import { parseSignedEurCents } from '@/lib/anchor';
import { formatCentsForInput } from '@/lib/loans';
import { formatMonthEt } from '@/lib/forecast';
import { priorityCell } from '@/lib/ledgerCells';
import { LINE_META, INCOME_CATS, COST_CATS } from './categoryMeta';
import {
  bucketsHaveForecast,
  forecastBaseClass,
  forecastEmphasisClass,
  formatEur,
  FORECAST_PILL_CLS,
  FORECAST_PILL_LABEL,
  FORECAST_PILL_TITLE,
  isoWeek,
  periodColumn,
  periodGroup,
  periodCertainty,
  periodIsNow,
  pickSum,
  settlementStatusLabel,
  type CashFlowEntry,
  type CreateEntrySeed,
  type DayRollup,
  type MoneyMode,
} from '@/lib/cashFlow';

/**
 * The direction-aware "unpaid" word the matrix appends to its overdue carry-forward
 * labels — a receivable is *laekumata*, a payable *maksmata*. Derived from the shared
 * register helper (`settlementStatusLabel`) so the two lenses name the same concept
 * identically and never drift (L14 — one truth per term).
 */
const unpaidWord = (kind: 'income' | 'cost'): string =>
  settlementStatusLabel('pending', kind).toLowerCase();

/**
 * The treasury matrix — the primary view's grid (replit.md "Primary view": the
 * cash-flow calendar is the hub; the grid gets maximum space). Module 1.3 breaks
 * the aggregate Tulu/Kulu rows of the 1.1 calendar into the 7 named lines: the
 * LINES are the rows, the PERIODS (Päev / Nädal / Kuu) are the columns, and the
 * running bank balance (RAHA PANGAKONTOL) is the bottom row — rendered 1:1 with
 * the approved canvas (artifacts/.../cash-flow-calendar/_kit.tsx CashMatrix +
 * SevenLines / WeekDrill / ViewDay / ViewYear).
 *
 * It NEVER sums money itself (L9 / master rule): period columns + balance come
 * from finance-core's `aggregatePeriods`, every line cell from `aggregateByLine`,
 * the "Kokku" column from `sumPeriods`. Each cell only SELECTS a field
 * (NET/GROSS) for display. The inline week drill expands one week into its
 * days using the SAME finance-core day rollups, so the running balance stays
 * continuous (a day-period's opening/closing is derived from `runningBalance`).
 */
const CERT_GLYPH = { actual: '●', committed: '◐', forecast: '○' } as const;
const CERT_LABEL = { actual: 'Kindel', committed: 'Kokkulepitud', forecast: 'Prognoos' } as const;

/* Single source of truth for the sticky row-name / KOKKU separator: a solid 2px
 * rule (pixel-identical in header, body and footer). The KOKKU column holds the
 * window totals and must never wrap. */
const STICKY_EDGE = '';
/* The frozen left pane = the row-label column (sticky left-0, 22rem wide) + the
 * KOKKU window-total column pinned right after it (sticky left-[22rem], matching
 * the label width). BOTH must be fully OPAQUE so the scrolling period columns
 * never bleed through them on horizontal scroll; KOKKU never wraps. The pane reads
 * as ONE block (no inner rule between Rida and KOKKU); a single solid 2px rule on
 * the KOKKU RIGHT edge is the only separator to the scrolling period columns —
 * pixel-identical in header, body and footer (canvas ground truth). */
const STICKY_RULE = 'border-r-2 border-r-[hsl(222_24%_40%)]';
const KOKKU_EDGE = `sticky left-[22rem] z-10 bg-background ${STICKY_RULE}`;
const KOKKU_W = 'min-w-[10rem] whitespace-nowrap';
/** Shared affordance for a clickable number → "open the register". Cells carry a
 *  `data-drill` marker (not a native `title`) so the OS tooltip never covers the
 *  matrix hover card. */
const DRILL_CELL = 'cursor-pointer hover:ring-1 hover:ring-inset hover:ring-[hsl(var(--primary))]/70';

export interface CashFlowTableProps {
  days: DayRollup[];
  entries: CashFlowEntry[];
  granularity: Granularity;
  mode: MoneyMode;
  /** Shared "rõhuta prognoos" toggle — washes forecast columns amber (L14 shared layer). */
  forecastEmphasis?: boolean;
  openingBalance: number;
  today: string;
  /** Bumped by the shared header "Täna" so the matrix re-centres on today's column. */
  centerNonce?: number;
  /**
   * Open the register (Ledger) filtered to a number's composition. EVERY
   * document-derived cell calls this with a `LedgerScope` (line × period, party,
   * Muud, Tulu/Kulu/KM/TSD/Rahavoog, overdue); the KOKKU header pill and the
   * whole-window "Rahavoog (kokku)" pass `null` (all documents). Balance rows
   * (Algsaldo / Lõppsaldo) never call it — a running bank balance carries the
   * LHV anchor and so cannot reconcile to a document filter.
   */
  onOpenScope?: (scope: LedgerScope | null) => void;
  /** Open one materialized KMD declaration's detail panel. KMD's row otherwise
   * drills to its source documents, which is useful for audit but cannot add a
   * payment event. */
  onOpenEntry?: (entryId: string) => void;
  /** Open the ONE shared create dialog seeded for a line (its kind + category). This is
   *  the table's lens-appropriate "+" — the SAME create surface the calendar day "+" and
   *  the register toolbar "+" open, so create is reachable from every lens, never one
   *  lens only (L14). The table owns no create state; it just hands the seed up. */
  onAddEntry?: (seed: CreateEntrySeed) => void;
  /** The shared "locate" target — when set, the matrix scrolls to + flashes the line
   *  that holds the matching document (via the shared useLocateScroll hook; L14). */
  activeMatch?: ActiveMatch | null;
  /** The shared reporting basis (Alus): cash placement (carry-forward aware) or
   *  accrual document-date placement. Under accrual the bank-balance rows are
   *  hidden — a running balance is a cash truth (L14 shared layer). */
  basis?: Basis;
  /** The shared plan-money scope (Prognoos: Väljas / Koos / Lahus). Under 'split'
   *  (the default) every forecast-containing cell breaks its plan share out as an
   *  amber "sh prognoos" sub-line and the balance rows carry the confirmed-only
   *  twin; 'blended' shows the merged numbers alone; 'excluded' never reaches this
   *  component with plan money (the shared view already dropped it) (L14). */
  forecastScope?: ForecastScope;
  /** Recorded EMTA declarations, month (`YYYY-MM`) → declared cents — the
   *  shared query layer's map (useKmdActuals). Feeds the KMD row's PAST
   *  declaration cells: the marked actual wins over the document backcast. */
  kmdActuals?: Record<string, number>;
  /** Record (`cents`) or clear (`null`) one month's declared KMD figure —
   *  the ✎ affordance on a past declaration cell. Omitted = read-only. */
  onSaveKmdActual?: (vars: { month: string; actualCents: number | null }) => void;
}

/** A signed amount cell: income green +, cost red −, auto by sign. When the
 *  finance-core sum behind it CONTAINS forecast money (`forecast`), the amount
 *  carries the shared tentative cue on exactly ONE line: with a "Lahus" plan
 *  sub-line the cue lives on the amber sub-line alone (the main figure keeps
 *  its sign color); without one, the main amount renders in the forecast amber
 *  (`--pending`, the same hue as the "○ Prognoos" pill and the emphasis wash) —
 *  so a plan never reads like a confirmed figure in the aggregate grid either
 *  (the calendar chip / register row mark the SAME state per-entry; L14).
 *  The +/− sign keeps the direction the hue no longer carries. The `plain` kind
 *  is the bank-balance row only, which is never forecast-marked (a running
 *  balance is an anchor projection, not a document sum). Display only — the
 *  number is untouched (L9). */
/** The optional second line under a matrix amount — the "Lahus" (split) scope's
 *  breakdown twin: an amber `sh prognoos ±X` (the finance-core forecast slice of
 *  the SAME sum the main figure shows) or a muted `kinnitatud X` (the confirmed
 *  balance twin on the bank-balance rows). Display only — every value arrives
 *  pre-computed from finance-core (L9). */
export interface NumSub {
  label: string;
  value: number;
  /** Unsigned muted rendering (the balance twin); default is the signed amber plan cue. */
  plain?: boolean;
}

function Num({ value, kind, bold, forecast = false, sub = null }: { value: number; kind: 'income' | 'cost' | 'auto' | 'plain'; bold?: boolean; forecast?: boolean; sub?: NumSub | null }) {
  // ONE amber line per cell: when the "Lahus" plan sub-line is present it ALONE
  // carries the tentative cue, and the main figure keeps its sign color so the
  // direction stays readable at a glance. Without a sub-line (the "Koos" scope,
  // or a zero plan slice) the amber main amount stays the cue — a plan must
  // never read like a confirmed figure.
  const tentative = forecast && !(sub && !sub.plain);
  const main =
    value === 0 ? (
      <span className="text-muted-foreground/40">–</span>
    ) : kind === 'plain' ? (
      <span className="tabular-nums text-foreground">{formatEur(value)}</span>
    ) : (
      (() => {
        const positive = kind === 'income' || (kind === 'auto' && value >= 0);
        const sign = positive ? '+' : '−';
        const color = tentative
          ? 'text-[hsl(var(--pending))]'
          : positive
            ? 'text-[hsl(var(--income))]'
            : 'text-[hsl(var(--cost))]';
        return (
          <span className={cn('tabular-nums', color, bold && 'font-semibold')}>
            {sign}
            {formatEur(Math.abs(value))}
          </span>
        );
      })()
    );
  if (!sub) return main;
  return (
    <span className="flex flex-col items-end gap-0">
      {main}
      <span
        className={cn(
          'whitespace-nowrap tabular-nums text-[10px] font-normal leading-tight',
          sub.plain ? 'text-muted-foreground' : 'text-[hsl(var(--pending))]',
        )}
      >
        {sub.label}{' '}
        {sub.plain ? formatEur(sub.value) : `${sub.value >= 0 ? '+' : '−'}${formatEur(Math.abs(sub.value))}`}
      </span>
    </span>
  );
}

/** Tiny grid glyph — signals the KOKKU cell opens the full ledger table. */
function TableGlyph() {
  return (
    <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true" className="shrink-0">
      <rect x="1.6" y="2.6" width="12.8" height="10.8" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <line x1="1.6" y1="6.2" x2="14.4" y2="6.2" stroke="currentColor" strokeWidth="1.2" />
      <line x1="6" y1="6.2" x2="6" y2="13.4" stroke="currentColor" strokeWidth="1.2" />
      <line x1="10" y1="6.2" x2="10" y2="13.4" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

function byCategory(lines: LineRollup[]): Record<CashFlowCategory, LineRollup> {
  const map = {} as Record<CashFlowCategory, LineRollup>;
  for (const line of lines) map[line.category] = line;
  return map;
}

/** A rendered column: a base period, or a single day spliced in by the drill. */
interface Column {
  key: string;
  from: string;
  to: string;
  label: string;
  sub: string;
  group: string;
  certainty: 'actual' | 'committed' | 'forecast';
  now: boolean;
  isDay: boolean;
  drillable: boolean;
  rollup: PeriodRollup;
}

export function CashFlowTable({ days, entries, granularity, mode, forecastEmphasis = false, openingBalance, today, centerNonce = 0, onOpenScope, onOpenEntry, onAddEntry, activeMatch = null, basis = 'cashBasis', forecastScope = 'split', kmdActuals = {}, onSaveKmdActual }: CashFlowTableProps) {
  const accrual = basis === 'accrualBasis';
  // "Prognoos: Lahus" — the split scope surfaces the plan share of every
  // forecast-containing sum as an amber sub-line (values from finance-core only).
  const split = forecastScope === 'split';
  const [hoverCol, setHoverCol] = useState<number | null>(null);
  const [openWeek, setOpenWeek] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<CashFlowCategory>>(() => new Set<CashFlowCategory>(['sale']));
  // The matrix cell hover — a DELAYED aggregate preview (useHoverIntent: the SAME
  // dwell the calendar/register use; L14). On intent the cell's EXACT drill set is
  // resolved with the SAME toRow + scope.match the click opens, so the preview's
  // count / Neto-KM-Bruto reconcile to the register header; an empty set shows
  // nothing. The click itself stays drill-to-register (onOpenScope) — unchanged.
  const [preview, setPreview] = useState<{ pos: HoverPos; label: string; docs: CashFlowEntry[] } | null>(null);
  const previewIntent = useHoverIntent<{ scope: LedgerScope | null; x: number; y: number }>(
    ({ scope, x, y }) => {
      const docs = scope === null ? entries : entries.filter((e) => scope.match(toRow(e, today, basis)));
      setPreview(
        docs.length > 0 ? { pos: { id: '', x, y }, label: scope?.label ?? 'Kõik dokumendid', docs } : null,
      );
    },
  );
  const startPreview = (scope: LedgerScope | null, x: number, y: number) =>
    previewIntent.start({ scope, x, y });
  const endPreview = () => {
    previewIntent.cancel();
    setPreview(null);
    setKmdHover(null);
  };
  // KMD backcast hover (1.12) — an INSTANT variance card (Prognoos / Tegelik /
  // Erinevus € + %) on the KMD row's PAST declaration cells, replacing the
  // delayed document preview there (the drill click still lists the documents).
  const [kmdHover, setKmdHover] = useState<{ pos: HoverPos; stats: KmdMonthStatView[] } | null>(null);
  // The "märgi tegelik" dialog — one past month's declared € figure being typed.
  const [kmdEdit, setKmdEdit] = useState<{ month: string; text: string } | null>(null);
  // The hover handlers for ONE drillable cell: a column cell also re-tints its
  // column (setHoverCol); the KOKKU cell (no colIdx) only previews. Leaving a cell
  // cancels the dwell and hides the card, so the preview tracks the rested cell.
  const hov = (scope: LedgerScope | null, colIdx?: number) => ({
    onMouseEnter: (ev: ReactMouseEvent<HTMLElement>) => {
      if (colIdx !== undefined) setHoverCol(colIdx);
      startPreview(scope, ev.clientX, ev.clientY);
    },
    onMouseLeave: endPreview,
  });

  const toggleExpanded = useCallback((cat: CashFlowCategory) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }, []);

  const periods = aggregatePeriods(days as CoreDay[], granularity);
  const coreEntries = entries as CoreEntry[];

  // Build the rendered columns: when a week is open (week granularity only),
  // replace that week with its days — both derived from the same finance-core
  // day rollups, so the running balance stays continuous across the splice.
  const columns: Column[] = [];
  for (const p of periods) {
    if (granularity === 'week' && openWeek === p.key) {
      const subset = (days as CoreDay[]).filter((d) => d.date >= p.from && d.date <= p.to);
      for (const dp of aggregatePeriods(subset, 'day')) {
        const col = periodColumn(dp, 'day');
        columns.push({
          key: `${p.key}-${dp.key}`,
          from: dp.from,
          to: dp.to,
          label: col.label,
          sub: col.sub,
          group: `Nädal ${isoWeek(p.from)} · päevad`,
          certainty: periodCertainty(dp, today),
          now: periodIsNow(dp, today),
          isDay: true,
          drillable: false,
          rollup: dp,
        });
      }
    } else {
      const col = periodColumn(p, granularity);
      columns.push({
        key: p.key,
        from: p.from,
        to: p.to,
        label: col.label,
        sub: col.sub,
        group: periodGroup(p, granularity),
        certainty: periodCertainty(p, today),
        now: periodIsNow(p, today),
        isDay: false,
        drillable: granularity === 'week',
        rollup: p,
      });
    }
  }

  // Auto-centre on the "today" column when the column set changes (and on load).
  const scrollRef = useRef<HTMLDivElement>(null);
  const centerToday = useCallback((smooth: boolean) => {
    const c = scrollRef.current;
    if (!c) return;
    const el = c.querySelector('[data-today="1"]') as HTMLElement | null;
    if (!el) return;
    c.scrollTo({ left: Math.max(0, el.offsetLeft - c.clientWidth / 2 + el.offsetWidth / 2), behavior: smooth ? 'smooth' : 'auto' });
  }, []);
  useEffect(() => {
    centerToday(false);
  }, [granularity, columns.length, centerToday]);
  // The shared header "Täna" bumps `centerNonce` → smooth-scroll to today's column
  // (skipped on first mount, where the structural effect above already centres).
  const centerNonceRef = useRef(centerNonce);
  useEffect(() => {
    if (centerNonceRef.current === centerNonce) return;
    centerNonceRef.current = centerNonce;
    centerToday(true);
  }, [centerNonce, centerToday]);

  // Reveal a "locate" target via the SAME shared hook the calendar + register use, so
  // Cmd+K / deep-link / "Leia" land identically in every lens (L14). The matrix has no
  // per-document row, so it tags the exact line × period cell the document sums into
  // (derived below) — the matrix twin of the calendar's per-chip reveal.
  useLocateScroll(scrollRef, activeMatch);
  // Derive the located entry's finance-core effective date and the column whose window
  // holds it, so the shared scroll+flash lands on the precise amount. resolveEntry (not
  // raw expectedDate) so an overdue item flashes the "today" column it was carried to —
  // exactly where the matrix sums it. No money math here (L9); this is a date lookup.
  const activeEntry = activeMatch ? (entries.find((e) => e.id === activeMatch.entryId) ?? null) : null;
  const activeDate = activeEntry ? resolveEntry(activeEntry as CoreEntry, today, basis).effectiveDate : null;
  const activeCol = activeDate ? columns.findIndex((c) => activeDate >= c.from && activeDate <= c.to) : -1;

  if (periods.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">Valitud vahemikus pole kirjeid</p>;
  }

  const windowTotal = sumPeriods(periods);
  const from = columns[0].from;
  const to = columns[columns.length - 1].to;
  const totalByCat = byCategory(aggregateByLine(coreEntries, { today, from, to, basis }));
  const perCol = columns.map((c) => byCategory(aggregateByLine(coreEntries, { today, from: c.from, to: c.to, basis })));

  // Carry-forward "Üle tähtaja" lens — overdue unpaid items finance-core rolled onto
  // today. The per-line slice (carriedForwardIncome/Cost) renders as the FIRST sub-row
  // under each line WHEN the line is expanded (OverdueLineRow, gated in CategoryRows).
  // It is NON-additive: these amounts already sit inside their category rows AND
  // Tulu/Kulu kokku (resolveEntry moved them to today), so they never re-sum into the
  // totals — which is why there is no separate consolidating "Üle tähtaja kokku" row.
  // Per-category presence (hasCatOverdue) is computed in CategoryRows straight off the
  // finance-core sums (display only — L9 + master rule; pickSum just picks net/vat/gross).

  // Per-category overdue count (entries finance-core carried to today). A count,
  // not a monetary sum, so it stays here — L9 governs money math, not tallies.
  const overdueCountByCat = {} as Record<CashFlowCategory, number>;
  for (const cat of CASH_FLOW_CATEGORIES) overdueCountByCat[cat] = 0;
  for (const e of coreEntries) {
    const r = resolveEntry(e, today, basis);
    if (r.carriedForward && r.effectiveDate >= from && r.effectiveDate <= to) {
      overdueCountByCat[e.category] += 1;
    }
  }

  const colW = granularity === 'month' || granularity === 'quarter' ? 'min-w-[7rem]' : 'min-w-[6rem]';
  // The display mode badge on the KOKKU pill — the toggle only offers neto/bruto.
  const modeTag = mode === 'net' ? 'neto' : 'bruto';
  const nowIdx = columns.findIndex((c) => c.now);

  // The inline counterparty breakdown for each expanded line — top-N named parties
  // plus a "Ülejäänud (N)" rollup — across the SAME rendered columns, so every sub-row
  // cell reconciles with its parent line cell (finance-core; L9 — no component sums).
  const colWindows = columns.map((c) => ({ from: c.from, to: c.to }));
  const partyMatrix = {} as Partial<Record<CashFlowCategory, LinePartyMatrix>>;
  for (const cat of CASH_FLOW_CATEGORIES) {
    if (expanded.has(cat)) {
      partyMatrix[cat] = aggregateLineParties(
        coreEntries as (CoreEntry & {
          priority: CashFlowEntry['priority'];
          project: CashFlowEntry['project'];
          paidCents: CashFlowEntry['paidCents'];
          adjustedCents: CashFlowEntry['adjustedCents'];
        })[],
        { today, category: cat, columns: colWindows, topN: 5, basis },
      );
    }
  }

  const groups: { group: string; span: number; dayGroup: boolean }[] = [];
  for (const c of columns) {
    const last = groups[groups.length - 1];
    if (last && last.group === c.group) last.span += 1;
    else groups.push({ group: c.group, span: 1, dayGroup: c.isDay });
  }

  // Vertical rules are drawn ONLY at group seams (and at the KOKKU→data edge),
  // never as a per-column hairline — the canvas rule: a seam runs unbroken through
  // the group header, the column header, the body and the footer, while columns
  // inside a group share an open grid (today is shown by its band, not a box).
  const newGroupAt = columns.map((c, i) => i === 0 || c.group !== columns[i - 1].group);

  // A line's value is its finance-core NET (income − cost) for the period — so a
  // bidirectional category (e.g. a loan drawdown vs. repayment) reconciles to
  // Bruto rahavoog instead of silently dropping its off-direction entries. The
  // subtraction happens in finance-core (`LineRollup.net`), never here (L9).
  const lineValue = (line: LineRollup) => pickSum(line.net, mode);

  // Does a line's finance-core sum CONTAIN forecast money (either direction) for
  // the displayed field? Drives the per-cell "○ + italic" tentative cue — a pure
  // zero-check over the certainty buckets via the shared predicate (L9: no
  // arithmetic; L14: the same "contains a plan" decision everywhere).
  const lineHasForecast = (line: LineRollup) =>
    bucketsHaveForecast(line.income, mode) || bucketsHaveForecast(line.cost, mode);

  // The "Lahus" sub-line of a line cell: the finance-core forecast slice of the
  // SAME net the cell shows (lineCertaintySlice — income − cost of the forecast
  // buckets). Null (no sub-line) when the scope isn't split or the slice is zero
  // for the displayed field. Selection only, never a sum here (L9).
  const lineForecastSub = (line: LineRollup): NumSub | null => {
    if (!split) return null;
    const v = pickSum(lineCertaintySlice(line, 'forecast'), mode);
    return v !== 0 ? { label: 'sh prognoos', value: v } : null;
  };
  // A footer cell's "sh prognoos" sub-line from a pre-computed finance-core slice.
  const forecastSub = (v: number): NumSub | null =>
    split && v !== 0 ? { label: 'sh prognoos', value: v } : null;
  // The balance rows' confirmed twin — shown when plan money makes the blended
  // closing balance differ from the confirmed-only one (both from finance-core).
  const confirmedSub = (confirmed: number, blended: number): NumSub | null =>
    split && confirmed !== blended ? { label: 'kinnitatud', value: confirmed, plain: true } : null;

  // Per-column forecast presence for the footer rows — each flag mirrors EXACTLY
  // the finance-core sum its row displays (Tulu = income buckets, Kulu = cost
  // buckets, Rahavoog = either; KMD mirrors `kmdDueFlow`'s own filter: the
  // materialized declaration entries (`taxKindOf` = KMD); TSD mirrors
  // `payrollTax`'s: payroll-line entries from the tax source). Booleans only —
  // never a re-sum (L9).
  const colIncomeForecast = perCol.map((m) => Object.values(m).some((l) => bucketsHaveForecast(l.income, mode)));
  const colCostForecast = perCol.map((m) => Object.values(m).some((l) => bucketsHaveForecast(l.cost, mode)));
  const colFlowForecast = perCol.map((m) => Object.values(m).some((l) => lineHasForecast(l)));
  const kmdForecastIn = (fromDay: string, toDay: string) =>
    coreEntries.some((e) => {
      if (taxKindOf(e) !== 'KMD' || e.certainty !== 'forecast') return false;
      const d = resolveEntry(e, today, basis).effectiveDate;
      return d >= fromDay && d <= toDay;
    });
  const colKmdForecast = columns.map((c) => kmdForecastIn(c.from, c.to));
  const tsdForecastIn = (fromDay: string, toDay: string) =>
    coreEntries.some((e) => {
      if (e.category !== 'payroll' || e.source !== 'tax' || e.certainty !== 'forecast') return false;
      const d = resolveEntry(e, today, basis).effectiveDate;
      return d >= fromDay && d <= toDay;
    });
  const colTsdForecast = columns.map((c) => tsdForecastIn(c.from, c.to));

  // Temporal orientation of the grid (the canvas rule stands: bands, never a
  // per-column box). Today reads as a clear primary band; a PAST column dims as
  // history (hover lifts it back to full contrast for reading); a future column
  // keeps the muted forecast-zone wash. Display only.
  const bgFor = (i: number) =>
    hoverCol === i
      ? 'bg-accent/40'
      : columns[i].now
        ? 'bg-primary/[0.12]'
        : columns[i].certainty === 'forecast'
          ? forecastEmphasisClass(columns[i].certainty, forecastEmphasis) || forecastBaseClass(columns[i].certainty)
          : '';
  const cell = (i: number, py: string) =>
    cn(
      'px-2.5 text-right tabular-nums transition-colors',
      py,
      colW,
      i > 0 && newGroupAt[i] && 'border-l border-muted',
      bgFor(i),
      columns[i].certainty === 'actual' && hoverCol !== i && 'opacity-[0.65]',
    );

  // ---- drill: every document-derived number opens the register filtered to its
  // composition (issue: "iga number avab registri filtreeritud vaates"). A
  // `LedgerScope` is a PREDICATE over the SAME rows the Ledger lists — a row belongs
  // to a column when ANY of its finance-core placements (`resolvePlacements`) falls
  // in that window, the very slices the matrix cells aggregate: a partially-paid
  // document therefore surfaces both under its payment-month cell (paid slice) AND
  // under today's overdue cell (jääk slice), so the filtered rows reconcile to the
  // cell. The builders never sum money (L9); they only describe which rows belong.
  // A `null` scope opens the full, unscoped register ("Kõik dokumendid").
  const colTag = (c: Column) => `${c.label} · ${c.sub}`;
  const inWindow = (row: LedgerRow, c: Column) =>
    resolvePlacements(row.e as CoreEntry, today, basis).some((p) => p.date >= c.from && p.date <= c.to);

  const lineScope = (cat: CashFlowCategory, c?: Column): LedgerScope => ({
    label: c ? `${LINE_META[cat].label} · ${colTag(c)}` : `${LINE_META[cat].label} · kogu periood`,
    match: (row) => row.category === cat && (c ? inWindow(row, c) : true),
  });
  const partyScope = (cat: CashFlowCategory, name: string, c?: Column): LedgerScope => ({
    label: c ? `${LINE_META[cat].label} · ${name} · ${colTag(c)}` : `${LINE_META[cat].label} · ${name}`,
    match: (row) => row.category === cat && row.osapool === name && (c ? inWindow(row, c) : true),
  });
  const othersScope = (cat: CashFlowCategory, named: string[], c?: Column): LedgerScope => ({
    label: c ? `${LINE_META[cat].label} · muud · ${colTag(c)}` : `${LINE_META[cat].label} · muud`,
    match: (row) => row.category === cat && !named.includes(row.osapool) && (c ? inWindow(row, c) : true),
  });
  const directionScope = (kind: 'income' | 'cost', c?: Column): LedgerScope => ({
    label: `${kind === 'income' ? 'Tulu kokku' : 'Kulu kokku'}${c ? ` · ${colTag(c)}` : ' · kogu periood'}`,
    match: (row) => row.suund === kind && (c ? inWindow(row, c) : true),
  });
  // Rahavoog is every document in the window; the whole-period total spans them
  // all, so its KOKKU drill IS the full register (null → "Kõik dokumendid").
  // Under the accrual basis the same total row reads "Tulem" (a document-dated
  // result, not a cash movement) — the label follows the footer row.
  const flowWord = accrual ? 'Tulem' : 'Rahavoog';
  const flowScope = (c?: Column): LedgerScope | null =>
    c ? { label: `${flowWord} · ${colTag(c)}`, match: (row) => inWindow(row, c) } : null;
  // KMD / TSD are finance-core tax figures — the drill lists the CONTRIBUTING
  // documents, not a re-sum; the figure stays computed. The KMD drill is
  // DECLARATION-based: the column's due declaration(s) → the PREVIOUS month's
  // VAT-bearing mirror documents, through the shared `kmdDeclarationScope`
  // (the same builder the enriched KMD detail uses — L14). The entries prop
  // (not LedgerRows) carries the `externalId` the accrual month is parsed from.
  const kmdEntriesIn = (c?: Column) =>
    entries.filter(
      (e) =>
        taxKindOf(e) === 'KMD' &&
        (c
          ? resolvePlacements(e as CoreEntry, today, basis).some((p) => p.date >= c.from && p.date <= c.to)
          : true),
    );
  // A materialized KMD entry is the place where its dated payment events live.
  // Resolve by statutory due date (not cash placement): an unpaid past KMD is
  // deliberately carried forward by `resolveEntry`, but still belongs to the
  // declaration column in this INFO row.
  const materializedKmdEntryFor = (c: Column): CashFlowEntry | null => {
    const matches = entries.filter(
      (entry) =>
        taxKindOf(entry) === 'KMD' &&
        entry.expectedDate >= c.from &&
        entry.expectedDate <= c.to,
    );
    return matches.length === 1 ? matches[0] : null;
  };
  // PAST declaration months (1.12 backcast) — deadlines already behind us, so no
  // materialized entry exists; the figure is recomputed from the mirrored documents
  // (finance-core `kmdBackcastSummary`) unless the operator marked the ACTUAL
  // declared figure. December 2025 was never synced, so the January column's
  // deadline is excluded via the earliest-month guard (honest-empty over fake €0).
  // A month that DOES carry a materialized `tax:kmd:*` entry (paid/overridden
  // rows are protected history and survive past their deadline) is excluded —
  // `kmdDueFlow` already counts it, and counting it again via the backcast
  // would double the month (one truth per number).
  const materializedKmdMonths = new Set(kmdDeclarationMonths(entries));
  const kmdPastIn = (fromDay: string, toDay: string): string[] => {
    const mirroredMonths = kmdPastDeclarationMonths({
      from: fromDay,
      to: toDay,
      todayIso: today,
      earliestMonth: KMD_BACKCAST_EARLIEST_MONTH,
    });
    // December 2025 predates the document mirror and must never appear as an
    // invented €0 forecast. An operator-recorded declaration is different: it
    // is a real fact, so show it at its statutory deadline and let it remain
    // correctable even when no source documents exist for that month.
    const markedPreMirrorMonths = Object.keys(kmdActuals).filter((month) => {
      const due = kmdDueDate(month);
      return month < KMD_BACKCAST_EARLIEST_MONTH && due >= fromDay && due <= toDay && due < today;
    });
    return [...new Set([...mirroredMonths, ...markedPreMirrorMonths])]
      .sort()
      .filter((month) => !materializedKmdMonths.has(month));
  };
  const windowKmdStats = kmdBackcastStats(entries, kmdPastIn(from, to), kmdActuals);
  const kmdStatsFor = (c?: Column): KmdMonthStat[] => {
    if (!c) return windowKmdStats;
    const months = kmdPastIn(c.from, c.to);
    return windowKmdStats.filter((s) => months.includes(s.month));
  };
  const kmdScope = (c?: Column): LedgerScope =>
    kmdDeclarationScope(
      [...new Set([...kmdDeclarationMonths(kmdEntriesIn(c)), ...kmdStatsFor(c).map((s) => s.month)])].sort(),
      c ? colTag(c) : 'kogu periood',
    );
  const payrollScope = (c?: Column): LedgerScope => ({
    label: `TSD — palgamaksud${c ? ` · ${colTag(c)}` : ' · kogu periood'}`,
    match: (row) => row.category === 'payroll' && (c ? inWindow(row, c) : true),
  });
  // Maksekokkulepped is a VISUAL cross-category aggregate (the installment parts
  // already live inside their own lines, so this row NEVER adds to KOKKU) — the
  // drill lists the materialized installment rows the figure summed, keyed off
  // the SAME source filter finance-core's paymentPlanFlow applies (L14).
  const planScope = (c?: Column): LedgerScope => ({
    label: `Maksekokkulepped${c ? ` · ${colTag(c)}` : ' · kogu periood'}`,
    match: (row) => row.e.source === 'payment_plan' && (c ? inWindow(row, c) : true),
  });
  // Overdue (carry-forward) drills key off finance-core's placements — a row belongs
  // iff a CARRIED slice falls in the column window, the SAME signal that put its
  // amount in the carriedForward* sums — never the register's display `status`, so
  // the filtered rows reconcile to the cell: a partially-paid document's paid slice
  // (an actual movement in its payment column) never drags it into another column's
  // overdue drill. Per-line scope adds the category + direction.
  const carriedScope = (cat: CashFlowCategory, kind: 'income' | 'cost', c?: Column): LedgerScope => ({
    label: `${LINE_META[cat].label} · üle tähtaja · ${unpaidWord(kind)}${c ? ` · ${colTag(c)}` : ''}`,
    match: (row) =>
      row.category === cat &&
      row.suund === kind &&
      resolvePlacements(row.e as CoreEntry, today, basis).some(
        (p) => p.carriedForward && (c ? p.date >= c.from && p.date <= c.to : true),
      ),
  });

  function LineRow({ cat, idx }: { cat: CashFlowCategory; idx: number }) {
    const meta = LINE_META[cat];
    const rowTotal = lineValue(totalByCat[cat]);
    const isOpen = expanded.has(cat);
    return (
      <tr
        className={cn('[&>td]:border-b [&>td]:border-border/40 transition-colors hover:bg-accent/15', idx % 2 === 1 && 'bg-muted/5')}
      >
        <td className={cn('sticky left-0 z-10 w-[22rem] min-w-[22rem] max-w-[22rem] bg-background px-3 py-1.5', STICKY_EDGE)}>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => toggleExpanded(cat)}
              aria-expanded={isOpen}
              aria-label={isOpen ? `Ahenda ${meta.label} osapooled` : `Ava ${meta.label} osapooled`}
              className="flex h-4 w-4 shrink-0 items-center justify-center rounded text-[10px] text-muted-foreground hover:bg-accent"
            >
              {isOpen ? '▾' : '▸'}
            </button>
            <span className={cn('inline-block h-2.5 w-2.5 shrink-0 rounded-[2px]', meta.kind === 'income' ? 'bg-[hsl(var(--income))]' : 'bg-[hsl(var(--cost))]')} />
            <span className="min-w-0 truncate font-medium text-foreground" title={meta.label}>{meta.label}</span>
            {overdueCountByCat[cat] > 0 && (
              <span
                title="Üle tähtaja — kantud tänasesse"
                className="shrink-0 whitespace-nowrap rounded bg-[hsl(var(--cost))]/15 px-1.5 py-0.5 text-[10px] font-medium text-[hsl(var(--cost))]"
              >
                üle tähtaja · {overdueCountByCat[cat]}
              </span>
            )}
            {onAddEntry && (
              // The line's lens-appropriate "+" — seeds the shared dialog with THIS line's
              // direction + category (date defaults to today). Same create surface as the
              // calendar day "+" and the register "+", so create is never table-only (L14).
              <button
                type="button"
                aria-label={`Lisa ${meta.label} kirje`}
                title={`Lisa ${meta.label} kirje`}
                onClick={() => onAddEntry({ kind: meta.kind, category: cat })}
                className="ml-auto flex h-4 w-4 shrink-0 items-center justify-center rounded text-[12px] leading-none text-muted-foreground transition-colors hover:bg-accent hover:text-primary"
              >
                <span aria-hidden>+</span>
              </button>
            )}
          </div>
        </td>
        <td
          {...hov(lineScope(cat))}
          onClick={() => onOpenScope?.(lineScope(cat))}
          data-drill=""
          className={cn('px-2.5 py-1.5 text-right', KOKKU_EDGE, KOKKU_W, DRILL_CELL)}
        >
          <Num value={rowTotal} kind="auto" bold forecast={lineHasForecast(totalByCat[cat])} sub={lineForecastSub(totalByCat[cat])} />
        </td>
        {columns.map((c, i) => (
          <td
            key={c.key}
            data-entry={activeEntry && activeEntry.category === cat && i === activeCol ? activeEntry.id : undefined}
            {...hov(lineScope(cat, c), i)}
            onClick={() => onOpenScope?.(lineScope(cat, c))}
            data-drill=""
            className={cn(cell(i, 'py-1.5'), DRILL_CELL)}
          >
            <Num value={lineValue(perCol[i][cat])} kind="auto" forecast={lineHasForecast(perCol[i][cat])} sub={lineForecastSub(perCol[i][cat])} />
          </td>
        ))}
      </tr>
    );
  }

  /** A counterparty (or "Ülejäänud") sub-row under an expanded line. */
  function SubRow({
    cat,
    name,
    cells,
    total,
    priority,
    overdueCount,
    projectCodes,
    forecast = false,
    others,
  }: {
    cat: CashFlowCategory;
    name: string;
    cells: PartyCell[];
    total: PartyCell;
    /** The party's most urgent priority (null on the "Ülejäänud" rollup) — drives the ⚑. */
    priority: Priority | null;
    /** Carried-forward (overdue) entry count for this party (0 on "Ülejäänud"). */
    overdueCount: number;
    /** Project codes (tunnused) the party's in-window entries carry (empty on "Ülejäänud"). */
    projectCodes: string[];
    /** The party has at least one forecast (prognoos) entry — renders the SAME
     *  "○ Prognoos" pill the calendar chip / register row carry, so a planned
     *  counterparty row never reads confirmed (false on "Ülejäänud"; L14). */
    forecast?: boolean;
    /** When set, this is the "Ülejäänud" rollup — its cells exclude the named parties. */
    others?: string[];
  }) {
    // Attention parties (a priority flag OR overdue items) read as a marked group at
    // the top of the line — the SAME triage cues the register/calendar show (L14).
    const pf = priorityCell(priority);
    const attention = priority !== null || overdueCount > 0;
    return (
      <tr className="[&>td]:border-b [&>td]:border-border/30 bg-background/40 text-[11px] hover:bg-accent/20">
        <td
          className={cn(
            'sticky left-0 z-10 w-[22rem] min-w-[22rem] max-w-[22rem] bg-background py-1 pl-9 pr-3',
            STICKY_EDGE,
            attention && 'border-l-2 border-l-[hsl(var(--pending))]',
          )}
        >
          {others ? (
            // The long-tail "Ülejäänud (N)" bucket links straight into the register,
            // scoped to this whole line — the named top-N are listed above it.
            <button
              type="button"
              onClick={() => onOpenScope?.(othersScope(cat, others))}
              className="flex items-center gap-1.5 rounded text-muted-foreground transition-colors hover:text-foreground"
            >
              <span className="truncate italic">{name}</span>
              <span className="shrink-0 text-[hsl(var(--primary))]">→ registris</span>
            </button>
          ) : (
            <div className="flex items-center gap-2">
              {/* Each named counterparty carries its data source + its triage cues. */}
              <span className="truncate text-foreground/80">{name}</span>
              {forecast && (
                <span title={FORECAST_PILL_TITLE} className={FORECAST_PILL_CLS}>
                  {FORECAST_PILL_LABEL}
                </span>
              )}
              {pf && (
                <span
                  aria-label={`Prioriteet: ${pf.label}`}
                  title={`Prioriteet: ${pf.label}`}
                  className={cn('shrink-0 rounded px-1 py-0.5 text-[9px] font-semibold', pf.cls)}
                >
                  ⚑
                </span>
              )}
              {overdueCount > 0 && (
                <span
                  title={`Üle tähtaja — kantud tänasesse · ${overdueCount}`}
                  className="shrink-0 whitespace-nowrap rounded bg-[hsl(var(--cost))]/15 px-1 py-0.5 text-[9px] font-medium text-[hsl(var(--cost))]"
                >
                  ⏱ {overdueCount}
                </span>
              )}
              {projectCodes.length > 0 && (
                <span
                  title={
                    projectCodes.length === 1
                      ? `Projekt: ${projectCodes[0]}`
                      : `Projektid: ${projectCodes.join(', ')}`
                  }
                  className="shrink-0 whitespace-nowrap rounded bg-[hsl(var(--primary))]/15 px-1 py-0.5 text-[9px] font-medium text-[hsl(var(--primary))]"
                >
                  {projectCodes.length === 1 ? projectCodes[0] : `${projectCodes.length} projekti`}
                </span>
              )}
            </div>
          )}
        </td>
        <td
          {...hov(others ? othersScope(cat, others) : partyScope(cat, name))}
          onClick={() => onOpenScope?.(others ? othersScope(cat, others) : partyScope(cat, name))}
          data-drill=""
          className={cn('px-2.5 py-1 text-right', KOKKU_EDGE, KOKKU_W, DRILL_CELL)}
        >
          <Num value={pickSum(total.net, mode)} kind="auto" />
        </td>
        {cells.map((pc, i) => (
          <td
            key={columns[i].key}
            {...hov(others ? othersScope(cat, others, columns[i]) : partyScope(cat, name, columns[i]), i)}
            onClick={() => onOpenScope?.(others ? othersScope(cat, others, columns[i]) : partyScope(cat, name, columns[i]))}
            data-drill=""
            className={cn(cell(i, 'py-1'), DRILL_CELL)}
          >
            <Num value={pickSum(pc.net, mode)} kind="auto" />
          </td>
        ))}
      </tr>
    );
  }

  /** The overdue (carry-forward) slice of ONE line — rendered as the FIRST sub-row when
   *  the line is expanded, listing items finance-core rolled onto today. Non-additive
   *  (already inside the line AND Tulu/Kulu kokku): hatched cells + an "ei liideta juurde"
   *  cue. Values are finance-core LineRollup.carriedForward* sums SELECTED for the mode
   *  (display only — L9). It drills via finance-core's carried placements, not row.status. */
  function OverdueLineRow({ cat, kind }: { cat: CashFlowCategory; kind: 'income' | 'cost' }) {
    const field = kind === 'income' ? 'carriedForwardIncome' : 'carriedForwardCost';
    const total = pickSum(totalByCat[cat][field], mode);
    const label = `Üle tähtaja · ${unpaidWord(kind)}`;
    return (
      <tr className="[&>td]:border-b [&>td]:border-border/30 bg-background/40 text-[11px] hover:bg-accent/20">
        <td
          className={cn(
            'sticky left-0 z-10 w-[22rem] min-w-[22rem] max-w-[22rem] border-l-2 border-l-[hsl(var(--cost))] bg-background py-1 pl-9 pr-3',
            STICKY_EDGE,
          )}
        >
          <div className="flex items-center gap-2">
            <span aria-hidden className="shrink-0 text-[hsl(var(--cost))]">⏱</span>
            <span className="truncate text-foreground/80" title={label}>{label}</span>
            <span className="shrink-0 whitespace-nowrap text-[10px] text-muted-foreground/70">ei liideta juurde</span>
          </div>
        </td>
        <td
          {...hov(carriedScope(cat, kind))}
          onClick={() => onOpenScope?.(carriedScope(cat, kind))}
          data-drill=""
          className={cn('px-2.5 py-1 text-right', KOKKU_EDGE, KOKKU_W, DRILL_CELL)}
        >
          <Num value={total} kind={kind} />
        </td>
        {columns.map((c, i) => {
          const v = pickSum(perCol[i][cat][field], mode);
          return (
            <td
              key={c.key}
              {...hov(carriedScope(cat, kind, c), i)}
              onClick={() => onOpenScope?.(carriedScope(cat, kind, c))}
              data-drill=""
              className={cn(cell(i, 'py-1'), v !== 0 && 'pg-hatched', DRILL_CELL)}
            >
              <Num value={v} kind={kind} />
            </td>
          );
        })}
      </tr>
    );
  }

  function CategoryRows({ cat, idx }: { cat: CashFlowCategory; idx: number }) {
    const matrix = partyMatrix[cat];
    const kind = LINE_META[cat].kind;
    const overdueField = kind === 'income' ? 'carriedForwardIncome' : 'carriedForwardCost';
    const hasCatOverdue = pickSum(totalByCat[cat][overdueField], mode) !== 0;
    // Row-level forecast cue for a named counterparty: does the party carry ANY
    // forecast entry on this line? A certainty check over the entries themselves
    // (the SAME field the calendar chip / register row read; L14) — never money (L9).
    const partyHasForecast = (party: string) =>
      entries.some((e) => e.category === cat && e.party === party && e.certainty === 'forecast');
    return (
      <Fragment>
        <LineRow cat={cat} idx={idx} />
        {expanded.has(cat) && matrix && (
          <Fragment>
            {/* The line's overdue (carry-forward) slice opens the cascade: the FIRST
                sub-row, visible only while the line is expanded. */}
            {hasCatOverdue && <OverdueLineRow cat={cat} kind={kind} />}
            {matrix.parties.map((p) => (
              <SubRow
                key={`${cat}-${p.party}`}
                cat={cat}
                name={p.party}
                cells={p.cells}
                total={p.total}
                priority={p.priority}
                overdueCount={p.overdueCount}
                projectCodes={p.projectCodes}
                forecast={partyHasForecast(p.party)}
              />
            ))}
            {matrix.others && (
              <SubRow
                cat={cat}
                name={`Ülejäänud (${matrix.others.count})`}
                cells={matrix.others.cells}
                total={matrix.others.total}
                priority={null}
                overdueCount={0}
                projectCodes={[]}
                others={matrix.parties.map((p) => p.party)}
              />
            )}
          </Fragment>
        )}
      </Fragment>
    );
  }

  function TotalRow({
    label,
    tag,
    values,
    total,
    kind,
    strong,
    topRule,
    forecastFlags,
    forecastTotal = false,
    subs,
    subTotal = null,
    scopeFor,
    hoverStatsFor,
    cellActionFor,
    entryActionFor,
  }: {
    label: string;
    /** A small uppercase badge before the label (e.g. INFO / BRUTO). */
    tag?: string;
    values: number[];
    total: number;
    kind: 'income' | 'cost' | 'auto' | 'plain';
    /** Bold, full-contrast row (the running totals + the bank balance); the
     * informational rows (KM / TSD / Algsaldo) read dim by default. */
    strong?: boolean;
    /** A heavier rule above this row to mark the start of a footer block. */
    topRule?: boolean;
    /** Per-column "contains forecast money" flags (index-aligned with `values`) —
     *  the affected cells carry the shared ○ + italic tentative cue. Omitted on
     *  the bank-balance rows (an anchor projection, never a document sum). */
    forecastFlags?: boolean[];
    /** The whole-window KOKKU cell's "contains forecast money" flag. */
    forecastTotal?: boolean;
    /** Per-column "Lahus" sub-lines (index-aligned with `values`; null = none) —
     *  each a pre-computed finance-core slice, already gated on the split scope
     *  by the caller. Omitted entirely when the row has no breakdown twin. */
    subs?: (NumSub | null)[];
    /** The KOKKU cell's "Lahus" sub-line (null = none). */
    subTotal?: NumSub | null;
    /**
     * Maps a cell to its register scope — `c` is the column for a period cell or
     * `null` for the KOKKU (whole-period) cell. Returning a `LedgerScope` makes
     * the cell open the filtered register; returning `null` (or omitting the prop
     * entirely, as the bank-balance rows do) leaves the cell inert.
     */
    scopeFor?: (c: Column | null) => LedgerScope | null;
    /** KMD row only (1.12): a cell whose window holds PAST declaration months
     *  swaps the delayed document preview for the INSTANT forecast-vs-actual
     *  variance card. Returns the pre-built card view models (empty = normal
     *  document preview). */
    hoverStatsFor?: (c: Column | null) => KmdMonthStatView[];
    /** KMD row only (1.12): the ✎ "märgi tegelik" affordance — returns the ONE
     *  past declaration month a cell covers (null = no affordance: no past
     *  month, several months, or read-only). */
    cellActionFor?: (c: Column | null) => string | null;
    /** KMD's explicit payment-detail action. A regular cell click remains the
     * source-document drill; this button opens the declaration's own settlement
     * panel so the operator can book the real dated payment. */
    entryActionFor?: (c: Column) => CashFlowEntry | null;
  }) {
    const py = strong ? 'py-2' : 'py-1.5';
    const rule = topRule && 'border-t-2 border-t-[hsl(222_16%_32%)]';
    const labelCls = strong ? 'text-[13px] font-semibold text-foreground' : 'text-[11px] text-muted-foreground';
    // The PRESENCE of scopeFor decides clickability, not its return value: a
    // returned `null` is a valid payload (Rahavoog → the full register), while the
    // bank-balance rows omit scopeFor entirely and so stay inert.
    const drillable = scopeFor !== undefined;
    // Enter one cell: a past-declaration KMD cell shows the INSTANT variance
    // card; every other drillable cell keeps the delayed document preview.
    const enterCell = (c: Column | null, ev: ReactMouseEvent<HTMLElement>) => {
      const stats = hoverStatsFor?.(c) ?? [];
      if (stats.length > 0) {
        previewIntent.cancel();
        setPreview(null);
        setKmdHover({ pos: { id: '', x: ev.clientX, y: ev.clientY }, stats });
        return;
      }
      setKmdHover(null);
      if (drillable) startPreview(scopeFor(c), ev.clientX, ev.clientY);
    };
    // The ✎ "märgi tegelik" affordance — opens the mark dialog seeded with the
    // current figure; stopPropagation keeps the cell's drill click intact.
    const markButton = (c: Column | null) => {
      const month = cellActionFor?.(c);
      if (!month || !onSaveKmdActual) return null;
      return (
        <button
          type="button"
          data-testid={`kmd-mark-${month}`}
          title={`Märgi ${formatMonthEt(month)} tegelik KMD`}
          onClick={(ev) => {
            ev.stopPropagation();
            const existing = kmdActuals[month];
            setKmdEdit({ month, text: existing !== undefined ? formatCentsForInput(existing) : '' });
          }}
          className="mr-1 rounded border border-border px-1 text-[10px] text-muted-foreground opacity-60 hover:bg-accent hover:opacity-100"
        >
          ✎
        </button>
      );
    };
    const openEntryButton = (c: Column) => {
      const entry = entryActionFor?.(c);
      if (!entry || !onOpenEntry) return null;
      const [month] = kmdDeclarationMonths([entry]);
      if (!month) return null;
      return (
        <button
          type="button"
          data-testid={`kmd-open-${entry.id}`}
          aria-label={`Ava ${formatMonthEt(month)} KMD arveldus`}
          title={`Ava ${formatMonthEt(month)} KMD arveldus`}
          onClick={(ev) => {
            ev.stopPropagation();
            onOpenEntry(entry.id);
          }}
          className="mr-1 rounded border border-[hsl(var(--vatmode))]/50 px-1.5 py-0.5 text-[10px] font-medium text-[hsl(var(--vatmode))] hover:bg-[hsl(var(--vatmode))]/10"
        >
          Ava KMD
        </button>
      );
    };
    return (
      <tr>
        <td className={cn('sticky left-0 z-10 w-[22rem] min-w-[22rem] max-w-[22rem] border-b border-muted bg-background px-3', py, STICKY_EDGE, rule)}>
          <div className="flex items-center gap-2">
            {tag && (
              <span className="shrink-0 rounded bg-[hsl(var(--vatmode))]/15 px-1 text-[9px] uppercase tracking-wide text-[hsl(var(--vatmode))]">{tag}</span>
            )}
            <span className={cn('truncate', labelCls)} title={label}>{label}</span>
          </div>
        </td>
        <td
          onMouseEnter={drillable ? (ev) => enterCell(null, ev) : undefined}
          onMouseLeave={drillable ? endPreview : undefined}
          onClick={drillable ? () => onOpenScope?.(scopeFor(null)) : undefined}
          data-drill={drillable ? '' : undefined}
          className={cn('border-b border-muted px-2.5 text-right', py, KOKKU_EDGE, KOKKU_W, rule, drillable && DRILL_CELL)}
        >
          {markButton(null)}
          <Num value={total} kind={kind} bold={strong} forecast={forecastTotal} sub={subTotal} />
        </td>
        {values.map((v, i) => (
          <td
            key={columns[i].key}
            onMouseEnter={(ev) => {
              setHoverCol(i);
              enterCell(columns[i], ev);
            }}
            onMouseLeave={drillable ? endPreview : undefined}
            onClick={drillable ? () => onOpenScope?.(scopeFor(columns[i])) : undefined}
            data-drill={drillable ? '' : undefined}
            className={cn(cell(i, py), 'border-b border-muted', strong && 'text-[13px]', rule, drillable && DRILL_CELL)}
          >
            {openEntryButton(columns[i])}
            {markButton(columns[i])}
            <Num value={v} kind={kind} bold={strong} forecast={forecastFlags?.[i] ?? false} sub={subs?.[i] ?? null} />
          </td>
        ))}
      </tr>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* the matrix — legend + LHV indicators live in the shared view header */}
      <div
        ref={scrollRef}
        data-testid="cashflow-matrix-scroll"
        onMouseLeave={() => {
          setHoverCol(null);
          endPreview();
        }}
        className="min-h-0 flex-1 overflow-auto"
      >
        <table className="w-full border-separate border-spacing-0 text-xs">
          <thead>
            <tr>
              <th
                className={cn('sticky left-0 top-0 z-30 h-8 w-[22rem] min-w-[22rem] max-w-[22rem] border-b border-border bg-card px-2', STICKY_EDGE)}
              />
              <th className={cn('sticky left-[22rem] top-0 z-30 h-8 border-b border-border bg-card', STICKY_RULE, KOKKU_W)} />
              {groups.map((g, gi) => (
                <th
                  key={`${g.group}-${gi}`}
                  colSpan={g.span}
                  className={cn(
                    'sticky top-0 z-20 h-8 border-b border-border px-3 py-1.5 text-center text-[11px] font-medium uppercase tracking-wide',
                    gi > 0 && 'border-l border-muted',
                    g.dayGroup ? 'bg-card bg-gradient-to-b from-primary/10 to-primary/10 text-foreground' : 'bg-card text-muted-foreground',
                  )}
                >
                  {g.dayGroup ? (
                    <span className="inline-flex items-center gap-2">
                      {g.group}
                      <button type="button" onClick={() => setOpenWeek(null)} className="rounded border border-border px-1 text-[10px] hover:bg-accent" title="Ahenda tagasi nädalaks">
                        ‹ ahenda
                      </button>
                    </span>
                  ) : (
                    g.group
                  )}
                </th>
              ))}
            </tr>
            <tr>
              <th className={cn('sticky left-0 top-8 z-30 w-[22rem] min-w-[22rem] max-w-[22rem] border-b border-border bg-card px-3 py-2 text-left align-bottom font-medium text-muted-foreground', STICKY_EDGE)}>Hankija / Klient</th>
              <th className={cn('sticky left-[22rem] top-8 z-30 border-b border-border bg-card px-2 py-2 text-right align-bottom', STICKY_RULE, KOKKU_W)}>
                <button
                  type="button"
                  onClick={() => onOpenScope?.(null)}
                  title="Ava kõik dokumendid — sorteeri ja filtreeri"
                  className={cn(
                    'flex w-full cursor-pointer items-center justify-end gap-1.5 rounded-md border px-1.5 py-1 transition-colors',
                    mode === 'net'
                      ? 'border-[#2dd4bf]/35 bg-[#2dd4bf]/10 hover:bg-[#2dd4bf]/20'
                      : 'border-[hsl(var(--vatmode))]/35 bg-[hsl(var(--vatmode))]/10 hover:bg-[hsl(var(--vatmode))]/20',
                  )}
                >
                  <span className="flex items-center gap-1 font-medium text-foreground">
                    <TableGlyph />
                    Kokku
                  </span>
                  <span
                    className={cn(
                      'rounded px-1 text-[9px] font-semibold uppercase tracking-wide',
                      mode === 'net' ? 'bg-[#2dd4bf]/15 text-[#2dd4bf]' : 'bg-[hsl(var(--vatmode))]/15 text-[hsl(var(--vatmode))]',
                    )}
                  >
                    {modeTag}
                  </span>
                </button>
              </th>
              {columns.map((c, i) => (
                <th
                  key={c.key}
                  data-today={c.now ? '1' : undefined}
                  onMouseEnter={() => setHoverCol(i)}
                  onClick={c.drillable ? () => setOpenWeek(c.key) : undefined}
                  title={c.drillable ? 'Klõpsa — ava see nädal päevadeks' : undefined}
                  className={cn(
                    'sticky top-8 z-20 border-b border-border px-2.5 py-1.5 text-right transition-colors',
                    i > 0 && newGroupAt[i] && 'border-l border-muted',
                    colW,
                    c.now
                      ? 'bg-card bg-gradient-to-b from-primary/25 to-primary/25 shadow-[inset_0_2px_0_0_hsl(var(--primary))]'
                      : hoverCol === i
                        ? 'bg-card bg-gradient-to-b from-accent/40 to-accent/40'
                        : 'bg-card',
                    c.drillable && 'cursor-pointer hover:bg-accent',
                  )}
                >
                  <div className="flex flex-col items-end gap-0.5">
                    <span className="flex items-center gap-1">
                      {c.now && (
                        <span className="rounded-full bg-[hsl(var(--primary))] px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none tracking-wide text-[hsl(var(--primary-foreground))]">
                          Täna
                        </span>
                      )}
                      {c.drillable && <span aria-hidden className="text-[9px] text-muted-foreground">⊞</span>}
                      <span aria-hidden className="text-[8px] text-muted-foreground" title={CERT_LABEL[c.certainty]}>{CERT_GLYPH[c.certainty]}</span>
                      <span
                        className={cn(
                          'font-semibold',
                          c.now
                            ? 'text-[hsl(var(--primary))]'
                            : nowIdx >= 0 && i > nowIdx
                              ? 'text-[hsl(var(--vatmode))]'
                              : nowIdx >= 0 && i < nowIdx
                                ? 'text-muted-foreground'
                                : 'text-foreground',
                        )}
                      >
                        {c.label}
                      </span>
                    </span>
                    {c.sub !== c.group && <span className="text-[10px] font-normal text-muted-foreground">{c.sub}</span>}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {INCOME_CATS.map((cat, i) => <CategoryRows key={cat} cat={cat} idx={i} />)}
            {COST_CATS.map((cat, i) => <CategoryRows key={cat} cat={cat} idx={i} />)}
          </tbody>
          <tfoot>
            <TotalRow label="Tulu kokku" kind="income" strong topRule values={columns.map((c) => pickSum(c.rollup.income, mode))} total={pickSum(windowTotal.income, mode)} forecastFlags={colIncomeForecast} forecastTotal={Object.values(totalByCat).some((l) => bucketsHaveForecast(l.income, mode))} subs={columns.map((c) => forecastSub(pickSum(c.rollup.incomeForecast, mode)))} subTotal={forecastSub(pickSum(windowTotal.incomeForecast, mode))} scopeFor={(c) => directionScope('income', c ?? undefined)} />
            <TotalRow label="Kulu kokku" kind="cost" strong values={columns.map((c) => -pickSum(c.rollup.cost, mode))} total={-pickSum(windowTotal.cost, mode)} forecastFlags={colCostForecast} forecastTotal={Object.values(totalByCat).some((l) => bucketsHaveForecast(l.cost, mode))} subs={columns.map((c) => forecastSub(-pickSum(c.rollup.costForecast, mode)))} subTotal={forecastSub(-pickSum(windowTotal.costForecast, mode))} scopeFor={(c) => directionScope('cost', c ?? undefined)} />
            {/* KMD — DECLARATION-based (a month column shows the KMD falling due
                in it, i.e. the previous month's documents' VAT — the SAME
                materialized entry the Maksud line carries), so it is INFO-tagged
                and never summed into KOKKU (no double-count). PAST deadlines
                (1.12 backcast) add the document-recomputed figure — or the
                operator-marked ACTUAL declaration when present — with an
                instant Prognoos/Tegelik/Erinevus hover + a ✎ mark affordance. */}
            <TotalRow
              label="KMD — käibemaks · 20. kpv"
              tag="INFO"
              kind="auto"
              values={columns.map((c) => -pickSum(kmdDueFlow(coreEntries, { today, from: c.from, to: c.to, basis }), mode) - kmdBackcastShownTotal(kmdStatsFor(c)))}
              total={-pickSum(kmdDueFlow(coreEntries, { today, from, to, basis }), mode) - kmdBackcastShownTotal(windowKmdStats)}
              forecastFlags={colKmdForecast}
              forecastTotal={kmdForecastIn(from, to)}
              subs={columns.map((c) => forecastSub(-pickSum(kmdDueFlow(coreEntries, { today, from: c.from, to: c.to, basis, certainty: 'forecast' }), mode)))}
              subTotal={forecastSub(-pickSum(kmdDueFlow(coreEntries, { today, from, to, basis, certainty: 'forecast' }), mode))}
              scopeFor={(c) => kmdScope(c ?? undefined)}
              hoverStatsFor={(c) => kmdMonthStatViews(kmdStatsFor(c ?? undefined))}
              cellActionFor={(c) => {
                const stats = kmdStatsFor(c ?? undefined);
                return stats.length === 1 ? stats[0].month : null;
              }}
              entryActionFor={materializedKmdEntryFor}
            />
            <TotalRow
              label="TSD — palgamaksud · 10. kpv"
              tag="INFO"
              kind="auto"
              values={columns.map((c) => -payrollTax(coreEntries, { today, from: c.from, to: c.to, basis }).net)}
              total={-payrollTax(coreEntries, { today, from, to, basis }).net}
              forecastFlags={colTsdForecast}
              forecastTotal={tsdForecastIn(from, to)}
              subs={columns.map((c) => forecastSub(-payrollTax(coreEntries, { today, from: c.from, to: c.to, basis, certainty: 'forecast' }).net))}
              subTotal={forecastSub(-payrollTax(coreEntries, { today, from, to, basis, certainty: 'forecast' }).net)}
              scopeFor={(c) => payrollScope(c ?? undefined)}
            />
            {/* Maksekokkulepped — a visual cross-line aggregate of the materialized
                installment rows (already counted inside Otsekulud/Finantskulud etc.),
                so it is INFO-tagged and never summed into KOKKU (no double-count). */}
            <TotalRow
              label="Maksekokkulepped — tarnijate maksegraafikud"
              tag="INFO"
              kind="auto"
              values={columns.map((c) => -pickSum(paymentPlanFlow(coreEntries, { today, from: c.from, to: c.to, basis }), mode))}
              total={-pickSum(paymentPlanFlow(coreEntries, { today, from, to, basis }), mode)}
              scopeFor={(c) => planScope(c ?? undefined)}
            />
            <TotalRow label={accrual ? 'Tulem (kokku)' : 'Rahavoog (kokku)'} kind="auto" strong values={columns.map((c) => pickSum(c.rollup.net, mode))} total={pickSum(windowTotal.net, mode)} forecastFlags={colFlowForecast} forecastTotal={Object.values(totalByCat).some((l) => lineHasForecast(l))} subs={columns.map((c) => forecastSub(pickSum(c.rollup.netForecast, mode)))} subTotal={forecastSub(pickSum(windowTotal.netForecast, mode))} scopeFor={(c) => flowScope(c ?? undefined)} />
            {/* The bank-balance rows are a CASH truth (the LHV-anchored running
                balance) — under the accrual (document-date) basis they cannot
                reconcile with the columns above them, so they are hidden. */}
            {!accrual && (
              <TotalRow
                label="Algsaldo"
                tag="BRUTO"
                kind="plain"
                topRule
                values={columns.map((c) => c.rollup.openingBalance)}
                total={openingBalance}
              />
            )}
            {!accrual && (
              <TotalRow
                label="Lõppsaldo · raha pangakontol"
                tag="BRUTO"
                kind="auto"
                strong
                values={columns.map((c) => c.rollup.closingBalance)}
                total={windowTotal.closingBalance}
                subs={columns.map((c) => confirmedSub(c.rollup.confirmedClosingBalance, c.rollup.closingBalance))}
                subTotal={confirmedSub(windowTotal.confirmedClosingBalance, windowTotal.closingBalance)}
              />
            )}
          </tfoot>
        </table>
      </div>
      {preview && <MatrixHoverCard pos={preview.pos} label={preview.label} entries={preview.docs} />}
      {kmdHover && <KmdVarianceHoverCard pos={kmdHover.pos} stats={kmdHover.stats} />}
      {kmdEdit && onSaveKmdActual && (
        <KmdActualDialog
          month={kmdEdit.month}
          text={kmdEdit.text}
          hasExisting={kmdActuals[kmdEdit.month] !== undefined}
          onText={(text) => setKmdEdit({ month: kmdEdit.month, text })}
          onSave={(cents) => {
            onSaveKmdActual({ month: kmdEdit.month, actualCents: cents });
            setKmdEdit(null);
          }}
          onClear={() => {
            onSaveKmdActual({ month: kmdEdit.month, actualCents: null });
            setKmdEdit(null);
          }}
          onClose={() => setKmdEdit(null)}
        />
      )}
    </div>
  );
}

/**
 * "Märgi tegelik KMD" — the small mark dialog of one PAST declaration month:
 * the € figure READ OFF the submitted EMTA declaration (may be negative — a
 * refund month). The typed text is parsed by the shared `parseSignedEurCents`
 * (L9: integer cents; no arithmetic here) and saved through the shared query
 * layer; "Eemalda" clears the record so the cell falls back to the backcast.
 */
function KmdActualDialog({
  month,
  text,
  hasExisting,
  onText,
  onSave,
  onClear,
  onClose,
}: {
  month: string;
  text: string;
  hasExisting: boolean;
  onText: (text: string) => void;
  onSave: (cents: number) => void;
  onClear: () => void;
  onClose: () => void;
}) {
  const cents = parseSignedEurCents(text);
  const invalid = text.trim() !== '' && cents === null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose} role="presentation">
      <div
        role="dialog"
        aria-label={`Märgi ${formatMonthEt(month)} tegelik KMD`}
        className="w-80 rounded-md border border-border bg-card p-4 shadow-xl"
        onClick={(ev) => ev.stopPropagation()}
      >
        <div className="text-[13px] font-semibold text-foreground">Tegelik KMD — {formatMonthEt(month)}</div>
        <p className="mt-1 text-[11px] text-muted-foreground">
          Esitatud deklaratsiooni summa eurodes (tagastuskuul miinusega).
        </p>
        <input
          autoFocus
          value={text}
          onChange={(ev) => onText(ev.target.value)}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter' && cents !== null) onSave(cents);
            if (ev.key === 'Escape') onClose();
          }}
          placeholder="nt 4823,00"
          inputMode="decimal"
          className={cn(
            'mt-2 w-full rounded border bg-background px-2 py-1.5 text-right text-[13px] tabular-nums text-foreground outline-none focus:border-primary',
            invalid ? 'border-[hsl(var(--cost))]' : 'border-border',
          )}
        />
        {invalid && <div className="mt-1 text-[10px] text-[hsl(var(--cost))]">Vigane summa — kasuta kuju 1234,56</div>}
        <div className="mt-3 flex items-center justify-end gap-2">
          {hasExisting && (
            <button type="button" onClick={onClear} className="mr-auto rounded border border-border px-2 py-1 text-[11px] text-muted-foreground hover:bg-accent">
              Eemalda märge
            </button>
          )}
          <button type="button" onClick={onClose} className="rounded border border-border px-2 py-1 text-[11px] text-muted-foreground hover:bg-accent">
            Tühista
          </button>
          <button
            type="button"
            disabled={cents === null}
            onClick={() => cents !== null && onSave(cents)}
            className="rounded bg-primary px-2.5 py-1 text-[11px] font-medium text-primary-foreground disabled:opacity-50"
          >
            Salvesta
          </button>
        </div>
      </div>
    </div>
  );
}
